import type { FC, PropsWithChildren } from 'react';
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { SharingCall } from 'voximplant-websdk/Call/SharingCall';

import { useConferenceStore } from 'store/conference';
import { attachVOXConnectedEvent } from 'store/conference/events/attachVOXConnectedEvent';
import { attachVOXSharingCallEvents } from 'store/conference/events/attachVOXSharingCallEvents';
import { useCurrentUserStore } from 'store/currentUser';
import type { IUser } from 'store/declare';
import { useSizeStore } from 'store/size';

import { useAllUserList } from 'hooks/useAllUserList';

import { getDevicesPermissionMap } from 'utils/getDevicesPermissionMap';

import { useScreenShareMetrics } from './hooks/useScreenShareMetrics';

export type ControlType = 'mic' | 'cam' | 'screen' | 'leave';

type State = {
    [x in ControlType]: boolean;
};

interface IContext {
    /** Состояние элементов управления */
    controls: State;
    /** Клик по кнопке "Камера" */
    handleCameraClick: () => void;
    /** Клик по кнопке "Микрофон" */
    handleMicrophoneClick: () => void;
    /** Клик по кнопке "Демонстрация экрана" */
    handleScreenShareClick: () => void;
    /** Скрыть модальное окно "Покинуть конференцию" */
    hideLeaveModal: () => void;
    /** Обновить состояние элементов управления */
    setControls: React.Dispatch<React.SetStateAction<State>>;
    /** Показать модальное окно "Покинуть конференцию" */
    showLeaveModal: () => void;
}

const controlsInitialState: State = {
    cam: false,
    leave: false,
    // микрофон всегда включен при присоединении к конференции
    mic: true,
    screen: false,
};

const Context = createContext<IContext>({
    controls: controlsInitialState,
    handleCameraClick: () => undefined,
    handleMicrophoneClick: () => undefined,
    handleScreenShareClick: () => undefined,
    hideLeaveModal: () => undefined,
    setControls: () => undefined,
    showLeaveModal: () => undefined,
});

/** Состояние элементов управления */
export const useControlStore = () => useContext(Context);

let sharingCall: SharingCall | null = null;
let sharingCallEvents: ReturnType<typeof attachVOXSharingCallEvents> | null = null;

/** Прекратить демонстрацию экрана */
function stopSharing() {
    // @ts-ignore
    if (sharingCall?.state() === 'CONNECTED') {
        sharingCall?.hangup();
    }
    sharingCallEvents?.off();
    sharingCallEvents = null;
}

/** Получение функции обновления текущего пользователя */
function getCurrentUserStateUpdater(setter: React.Dispatch<React.SetStateAction<IUser | null>>) {
    return (newState: Partial<IUser>) => setter(prevState => (prevState ? { ...prevState, ...newState } : prevState));
}

const ControlsStore: FC<PropsWithChildren> = ({ children }) => {
    const [state, setState] = useState(controlsInitialState);
    const { VOXCallInstance, VOXInstance } = useConferenceStore();
    const [connected, setConnected] = useState(false);
    const { currentUser, setCurrentUser } = useCurrentUserStore();
    const { setIsFull } = useSizeStore();
    const currentUserStateUpdater = getCurrentUserStateUpdater(setCurrentUser);
    const { screenShareUser } = useAllUserList();

    const handleMicrophoneClick = useCallback(() => {
        if (connected && VOXCallInstance) {
            setState(controls => {
                const newMicrophoneState = !controls.mic;

                if (newMicrophoneState) {
                    VOXCallInstance.unmuteMicrophone();
                } else {
                    VOXCallInstance.muteMicrophone();
                }
                currentUserStateUpdater({ isMuted: !newMicrophoneState });

                return { ...controls, mic: newMicrophoneState };
            });
        }
    }, [connected, VOXCallInstance]);

    const hideLeaveModal = useCallback(() => {
        setState(controls => ({ ...controls, leave: false }));
    }, []);

    const showLeaveModal = useCallback(() => {
        setState(controls => ({ ...controls, leave: true }));
    }, []);

    const stopShareScreen = useCallback(() => {
        // Повторное нажатие на кнопку прервет демонстрацию экрана
        stopSharing();
        setState(controls => ({ ...controls, screen: false }));
        // Выключаем режим "Во весь экран"
        setIsFull(false);
    }, []);

    const handleScreenShareClick = useCallback(() => {
        if (connected && VOXCallInstance) {
            if (state.screen) {
                // Повторное нажатие на кнопку прервет демонстрацию экрана
                stopShareScreen();
            } else {
                sharingCall = VOXInstance.joinAsSharing(VOXCallInstance.number());
                sharingCallEvents = attachVOXSharingCallEvents(sharingCall, result => {
                    setState(controls => ({ ...controls, screen: result }));
                    // отмена демонстрации экрана из панели браузера
                    if (sharingCall && !result) stopSharing();
                });
                sharingCallEvents.on();
            }
        }
    }, [VOXCallInstance, connected, state.screen, stopShareScreen]);

    const handleCameraClick = useCallback(() => {
        if (connected && VOXCallInstance) {
            setState(controls => {
                const newCameraState = !controls.cam;

                VOXInstance.showLocalVideo(newCameraState);
                VOXCallInstance.sendVideo(newCameraState);

                return { ...controls, cam: newCameraState };
            });
        }
    }, [connected, VOXCallInstance]);

    const contextValue = useMemo<IContext>(() => {
        return {
            controls: state,
            handleCameraClick,
            handleMicrophoneClick,
            handleScreenShareClick,
            hideLeaveModal,
            setControls: setState,
            showLeaveModal,
        };
    }, [state, handleScreenShareClick, handleMicrophoneClick, handleCameraClick]);

    useScreenShareMetrics(state.screen);

    // отправка состояния микрофона в vox
    useEffect(() => {
        if (VOXCallInstance && connected) {
            const message = JSON.stringify({
                microphoneStatus: {
                    id: currentUser?.id,
                    state: state.mic,
                },
            });

            VOXCallInstance?.sendMessage(message);
        }
    }, [state.mic, currentUser?.id, VOXCallInstance, connected]);

    // включаем камеру при присоединении к конференции, если пользователь дал разрешение на использование камеры
    useEffect(() => {
        (async () => {
            const { camera } = await getDevicesPermissionMap(['camera']);

            if (VOXCallInstance && connected && camera) {
                VOXInstance.showLocalVideo(true);
                setState(controls => ({ ...controls, cam: true }));
            }
        })();
    }, [VOXCallInstance, connected]);

    useEffect(() => {
        if (VOXCallInstance) {
            attachVOXConnectedEvent(VOXCallInstance, () => setConnected(true));
        }
    }, [VOXCallInstance]);

    // переключение состояния "Демонстрирует экран" у текущего пользователя
    useEffect(() => {
        currentUserStateUpdater({ isSharing: state.screen });
    }, [state.screen]);

    // демонстрировать экран может только один пользователь
    // если пользователь 1 начинает демонстрацию экрана в том время как пользователь 2 уже демонстрирует экран
    // то у пользователя 1 нужно выключить демонстрацию экрана
    useEffect(() => {
        const screenShareUserIsChanged = screenShareUser?.id !== currentUser?.id;
        // @ts-ignore
        const screenShareCallIsConnected = sharingCall?.state() === 'CONNECTED';
        const currentUserScreenShareStopCondition = screenShareUserIsChanged && screenShareCallIsConnected;

        if (currentUserScreenShareStopCondition) stopShareScreen();
    }, [screenShareUser?.id, currentUser?.id, stopShareScreen]);

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export default memo(ControlsStore);
